import { Controller } from 'stimulus';
import { normaliseFilterOptions } from './tabulator_table/filters';
import { extractFilterKey } from './tabulator_table/helpers';

// The breakdown table's device-model column — the field a per-model open
// narrows, and the one whose checkbox filter the user can widen again.
const DEVICE_MODEL_FIELD = 'device_model';

/**
 * Opens the device breakdown modal scoped to one device model.
 *
 * A line-item row carries `data-device-model-name`; opening from it pre-applies
 * that model as a checkbox filter on the breakdown table, exactly as if the
 * user had ticked it in the column's filter popup — so the filter button shows
 * as active and can be widened from the popup. Opening from the Device Data
 * card (no data attribute) clears the filter and shows every row. The filter is
 * cleared again when the modal closes, so the next open starts from the list
 * the trigger asks for.
 *
 * The subtitle only describes the full list, so it is hidden while a model
 * filter is active — the constraint is implied rather than explanatory.
 */
export default class extends Controller {
  static targets = ['subtitle'];

  connect() {
    this.boundHandleShow = this.handleShow.bind(this);
    this.boundHandleHide = this.handleHide.bind(this);
    this.element.addEventListener('show.bs.modal', this.boundHandleShow);
    this.element.addEventListener('hidden.bs.modal', this.boundHandleHide);
  }

  disconnect() {
    this.element.removeEventListener('show.bs.modal', this.boundHandleShow);
    this.element.removeEventListener('hidden.bs.modal', this.boundHandleHide);
  }

  handleShow(event) {
    this.applyModelFilter(event.relatedTarget?.dataset?.deviceModelName || '');
  }

  handleHide() {
    this.applyModelFilter('');
  }

  applyModelFilter(modelName) {
    const table = this.tableController;
    if (!table?.tabulator) return;

    if (modelName) {
      table.setFilterCriteria(DEVICE_MODEL_FIELD, 'checkbox', {
        kind: 'checkbox',
        checkedValues: new Set([modelName]),
        allOptions: this.modelOptions(table),
      });
    } else {
      table.filterState.delete(DEVICE_MODEL_FIELD);
    }
    table.applyFilters();

    if (this.hasSubtitleTarget) {
      this.subtitleTarget.classList.toggle('d-none', Boolean(modelName));
    }
  }

  // The column's full option list, in the same shape the filter popup derives
  // from the table's data — the checkbox matcher reads "is anything
  // unchecked?" off it, so a partial list would read as no filter at all.
  modelOptions(table) {
    const values = new Set();
    table.tabulator.getData().forEach((row) => {
      const key = extractFilterKey(row[DEVICE_MODEL_FIELD]);
      if (key) values.add(key);
    });
    return normaliseFilterOptions(
      [...values].sort((a, b) => a.localeCompare(b))
    );
  }

  get tableController() {
    const el = this.element.querySelector(
      '[data-controller~="tabulator-table"]'
    );
    if (!el) return null;
    return this.application.getControllerForElementAndIdentifier(
      el,
      'tabulator-table'
    );
  }
}
